import { IReactionDisposer, makeObservable, observable, action, computed, reaction, runInAction } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { uniqBy } from 'lodash';
import {
    NodeBaseDto,
    ActionParams,
    ContractComparisonParameters,
    ContractComparisonSectionsResultDto
} from '../../types';
import { InputBaseModel, NodeBaseModel, ComparisonNodeBaseModel } from '..';
import { InstanceUtils } from '../../misc';
import { NodeFactory, ComparisonNodeFactory } from '../../factories';
import { TypeUtils } from '../../../common/misc';
import { SourceStore } from '../../../custom_shared/stores';
import { NodeIds, InputIds } from '../../misc';
import ContractComparisonStore from '../../stores/ContractComparisonStore';
import SectionMatchingModel from './SectionMatchingModel';
import {
    ContractComparisonSummaryPayloadBuilder,
    SummaryPayloadState
} from '../../misc/ContractComparisonSummaryPayloadBuilder';

const tooltipTitles = {
    failedToFetch: 'Failed to fetch previous contract',
    missingBusinessId: 'Enter Business ID manually to search.'
} as const;

const summaryPreparationTimeoutMs = 15_000;
const summaryRegenerationDelayMs = 300;
const summaryRefreshPollIntervalMs = 100;

type CompareActionState = 'missingBusinessId' | 'readyToPrepare' | 'preparing' | 'readyToCompare' | 'notFound';

type FetchPreviousContractStatus = 'idle' | 'failed' | 'notFound';

type CompareActionIcon = 'loading' | 'check' | null;

type CompareActionViewModel = {
    state: CompareActionState;
    label: string;
    tooltipTitle: string | null;
    isClickable: boolean;
    icon: CompareActionIcon;
};

export default class ContractComparisonModel {
    newContractNodes: NodeBaseModel[] = [];

    previousContractNodes: NodeBaseModel[] = [];

    comparisonNodes: ComparisonNodeBaseModel[] = [];

    sectionMatching: SectionMatchingModel | null = null;

    generatedSummary: string | null = null;

    fetchPreviousContractStatus: FetchPreviousContractStatus = 'idle';

    isLoading: boolean = false;

    isSelected: boolean = false;

    isExecutingAction: boolean = false;

    isSummaryLoading: boolean = false;

    private fetchDataCancelSource: CancelTokenSource | null = null;

    private summaryCancelSource: CancelTokenSource | null = null;

    private lastRequestedSummaryPayloadHash: number | null = null;

    private lastCompletedSummaryPayloadHash: number | null = null;

    private lastGeneratedSummaryPayloadHash: number | null = null;

    private summaryRefreshPromise: Promise<void> | null = null;

    private summaryRefreshPayloadHash: number | null = null;

    private readonly summaryRegenerationDisposer: IReactionDisposer;

    constructor(
        private readonly store: ContractComparisonStore,
        private readonly sourceStore: SourceStore,
        private readonly businessIdInput: InputBaseModel,
        readonly parameters: ContractComparisonParameters
    ) {
        makeObservable<
            ContractComparisonModel,
            | 'lastRequestedSummaryPayloadHash'
            | 'lastCompletedSummaryPayloadHash'
            | 'lastGeneratedSummaryPayloadHash'
            | 'summaryPayloadState'
        >(this, {
            newContractNodes: observable,
            previousContractNodes: observable,
            comparisonNodes: observable,
            sectionMatching: observable.ref,
            generatedSummary: observable,
            isLoading: observable,
            fetchPreviousContractStatus: observable,
            isSelected: observable,
            isExecutingAction: observable,
            isSummaryLoading: observable,
            lastRequestedSummaryPayloadHash: observable,
            lastCompletedSummaryPayloadHash: observable,
            lastGeneratedSummaryPayloadHash: observable,
            setIsSelected: action,
            close: action,
            reset: action,
            businessId: computed,
            hasPreviousContractNodes: computed,
            newContractInputs: computed,
            newContractInputsWithSource: computed,
            previousContractInputs: computed,
            previousContractInputsWithSource: computed,
            newContractBusinessTypeInput: computed,
            previousContractBusinessTypeInput: computed,
            isAnyInputLoading: computed,
            hasFreshSummary: computed,
            hasResolvedSummaryForCurrentPayload: computed,
            summaryPayloadHash: computed,
            summaryPayloadState: computed,
            businessTypeConflict: computed,
            canSaveChanges: computed,
            compareAction: computed
        });

        this.summaryRegenerationDisposer = reaction(
            () => ({
                hasResolvedSummaryForCurrentPayload: this.hasResolvedSummaryForCurrentPayload,
                isAnyInputLoading: this.isAnyInputLoading,
                isComparisonSummaryConfigured: this.store.isComparisonSummaryConfigured,
                isLoading: this.isLoading,
                isSelected: this.isSelected,
                summaryPayloadHash: this.summaryPayloadHash
            }),
            summaryState => {
                if (
                    !summaryState.isSelected ||
                    summaryState.isLoading ||
                    summaryState.isAnyInputLoading ||
                    !summaryState.isComparisonSummaryConfigured ||
                    summaryState.summaryPayloadHash === null ||
                    summaryState.hasResolvedSummaryForCurrentPayload
                ) {
                    return;
                }

                this.generateContractComparisonSummary();
            },
            {
                delay: summaryRegenerationDelayMs
            }
        );
    }

    private static getBusinessType(nodes: NodeBaseModel[]): InputBaseModel | undefined {
        const generalInformationNode = nodes.find(node => node.id === NodeIds.GeneralInformation);

        if (!InstanceUtils.isBasicNode(generalInformationNode)) {
            return undefined;
        }

        return generalInformationNode.getInputById(InputIds.GeneralInformation.BusinessType);
    }

    private static wait(timeoutMs: number) {
        return new Promise<void>(resolve => {
            setTimeout(resolve, timeoutMs);
        });
    }

    get businessId() {
        return TypeUtils.isString(this.businessIdInput.value) ? this.businessIdInput.value.trim() : null;
    }

    get hasPreviousContractNodes() {
        return this.previousContractNodes.length > 0;
    }

    get newContractInputs() {
        return this.newContractNodes.flatMap(node => node.inputs);
    }

    get newContractInputsWithSource() {
        return this.newContractInputs.filter(InstanceUtils.isInputWithSource);
    }

    get previousContractInputs() {
        return this.previousContractNodes.flatMap(node => node.inputs);
    }

    get previousContractInputsWithSource() {
        return this.previousContractInputs.filter(InstanceUtils.isInputWithSource);
    }

    get newContractBusinessTypeInput() {
        return ContractComparisonModel.getBusinessType(this.newContractNodes);
    }

    get previousContractBusinessTypeInput() {
        return ContractComparisonModel.getBusinessType(this.previousContractNodes);
    }

    get isAnyInputLoading(): boolean {
        return (
            this.newContractInputsWithSource.some(input => input.isLoading) ||
            this.previousContractInputsWithSource.some(input => input.isLoading)
        );
    }

    get businessTypeConflict(): string | null {
        const newContractBusinessTypeInput = this.newContractBusinessTypeInput;
        const previousContractBusinessTypeInput = this.previousContractBusinessTypeInput;

        if (
            !InstanceUtils.isInputWithSource(newContractBusinessTypeInput) ||
            !InstanceUtils.isInputWithSource(previousContractBusinessTypeInput)
        ) {
            return null;
        }

        if (newContractBusinessTypeInput.value === previousContractBusinessTypeInput.value) {
            return null;
        }

        const newTypeLabel = newContractBusinessTypeInput.formattedValue;
        const previousTypeLabel = previousContractBusinessTypeInput.formattedValue;

        return `Business type mismatch. You can't save changes because the previous type (${previousTypeLabel}) doesn't match the current type (${newTypeLabel}).`;
    }

    setIsSelected(isSelected: boolean) {
        this.isSelected = isSelected;
    }

    close() {
        // Closing discards the matching draft, while preserving confirmed matches and field values.
        this.sectionMatching?.cancelEdit();
        this.setIsSelected(false);
    }

    get canSaveChanges() {
        const matching = this.sectionMatching;

        // Sectioned comparisons need a confirmed mapping before field changes can be saved.
        if (matching && (!matching.isConfirmed || matching.isConfirming)) {
            return false;
        }

        return !this.businessTypeConflict && !this.isExecutingAction;
    }

    get hasFreshSummary(): boolean {
        return (
            !!this.generatedSummary &&
            this.summaryPayloadHash !== null &&
            this.lastGeneratedSummaryPayloadHash === this.summaryPayloadHash
        );
    }

    get hasResolvedSummaryForCurrentPayload(): boolean {
        return this.summaryPayloadHash !== null && this.lastCompletedSummaryPayloadHash === this.summaryPayloadHash;
    }

    get summaryPayloadHash(): number | null {
        return this.summaryPayloadState?.signatureHash ?? null;
    }

    get compareAction(): CompareActionViewModel {
        if (!this.businessId) {
            return {
                state: 'missingBusinessId',
                label: 'Compare with previous period?',
                tooltipTitle: tooltipTitles.missingBusinessId,
                isClickable: false,
                icon: null
            };
        }

        if (this.isLoading) {
            return {
                state: 'preparing',
                label: 'Preparing previous contract...',
                tooltipTitle: null,
                isClickable: false,
                icon: 'loading'
            };
        }

        if (this.hasPreviousContractNodes) {
            return {
                state: 'readyToCompare',
                label: 'Previous contract ready to compare',
                tooltipTitle: null,
                isClickable: true,
                icon: 'check'
            };
        }

        if (this.fetchPreviousContractStatus === 'notFound') {
            return {
                state: 'notFound',
                label: 'Previous contract not found',
                tooltipTitle: null,
                isClickable: false,
                icon: null
            };
        }

        return {
            state: 'readyToPrepare',
            label: 'Compare with previous period',
            tooltipTitle: this.fetchPreviousContractStatus === 'failed' ? tooltipTitles.failedToFetch : null,
            isClickable: true,
            icon: null
        };
    }

    async executeContractComparisonAction(actionId: string, actionParams: ActionParams = {}) {
        try {
            runInAction(() => {
                this.isExecutingAction = true;
            });

            const response = await this.store.executeContractComparisonAction(this.newContractNodes, actionId, {
                ...actionParams,
                LayerId: this.parameters.layerId
            });

            if (!response.isOk()) {
                return;
            }

            response.map(nodes =>
                runInAction(() => {
                    try {
                        this.newContractNodes = NodeFactory.createOrUpdateNodes(
                            nodes,
                            this.newContractNodes,
                            this.sourceStore.sources
                        );

                        if (this.parameters.layerId) {
                            this.filterComparisonLayers(this.newContractNodes, this.parameters.layerId);
                        }

                        this.sectionMatching?.refresh();
                    } catch (error) {
                        console.error('Error while creating or updating new contract nodes: ', error);
                    }

                    this.newContractInputsWithSource
                        .filter(input => !input.useCachedSourceOptions)
                        .forEach(input => input.fetchSourceOptions());

                    this.refreshSummaryState();
                })
            );
        } catch (error) {
            console.error('Error while executing contract comparison action: ', error);
        } finally {
            runInAction(() => {
                this.isExecutingAction = false;
            });
        }
    }

    async generateContractComparisonSummary() {
        if (!this.store.isComparisonSummaryConfigured) {
            return;
        }

        const targetPayloadHash = this.summaryPayloadHash;

        if (
            targetPayloadHash === null ||
            this.hasFreshSummary ||
            this.lastCompletedSummaryPayloadHash === targetPayloadHash
        ) {
            return;
        }

        try {
            const summaryRefreshPromise = this.ensureSummaryRefreshPromise(targetPayloadHash);
            await summaryRefreshPromise;
        } catch (error) {
            if (!axios.isCancel(error)) {
                console.error('Error while generating contract comparison summary: ', error);
            }
        }
    }

    async openOrLoad(nodes: NodeBaseDto[]) {
        if (!this.hasPreviousContractNodes) {
            await this.prepareComparison(nodes);
            return;
        }

        this.refreshComparison(nodes);
        this.setIsSelected(true);
    }

    saveChanges() {
        this.store.saveContractComparisonChanges(this);
    }

    reset() {
        this.sectionMatching?.reset();
        this.sectionMatching = null;
        this.cancelFetchDataRequest();
        this.cancelSummaryRequest();
        this.summaryRefreshPromise = null;
        this.summaryRefreshPayloadHash = null;
        this.disposeComparisonNodes();
        this.newContractNodes = [];
        this.previousContractNodes = [];
        this.comparisonNodes = [];
        this.fetchPreviousContractStatus = 'idle';
        this.isLoading = false;
        this.isSelected = false;
        this.isSummaryLoading = false;
        this.clearSummaryState();
    }

    dispose() {
        this.summaryRegenerationDisposer();
        this.reset();
    }

    private disposeComparisonNodes() {
        this.comparisonNodes.forEach(comparisonNode => comparisonNode.dispose());
    }

    private createComparisonNodes() {
        this.disposeComparisonNodes();

        const matching = this.sectionMatching;

        runInAction(() => {
            // Some nodes may be added or removed, so we need to get all unique node ids and names to display changes
            this.comparisonNodes = uniqBy([...this.newContractNodes, ...this.previousContractNodes], 'id')
                .map(node => ({
                    nodeId: node.id,
                    nodeName: node.name
                }))
                .map(node =>
                    ComparisonNodeFactory.createComparisonNode({
                        nodeId: node.nodeId,
                        nodeName: node.nodeName,
                        newNode: this.newContractNodes.find(n => n.id === node.nodeId),
                        previousNode: this.previousContractNodes.find(n => n.id === node.nodeId),
                        layerId: this.parameters.layerId,
                        getSectionPairs: matching ? () => matching.sectionPairs : undefined
                    })
                )
                .filter(InstanceUtils.isComparisonNode);
            this.refreshSummaryState();
        });
    }

    private initializeNewContractNodes(nodes: NodeBaseDto[]) {
        runInAction(() => {
            this.newContractNodes = NodeFactory.createOrUpdateNodes(nodes, [], this.sourceStore.sources);

            const layerId = this.parameters.layerId;

            if (layerId) {
                this.filterComparisonLayers(this.newContractNodes, layerId);
            }

            this.newContractInputs.forEach(input => {
                input.setIsControlsHidden(true);

                if (InstanceUtils.isInputWithSource(input)) {
                    input.fetchSourceOptions();
                }
            });
        });
    }

    private initializePreviousContractNodes(nodes: NodeBaseDto[]) {
        runInAction(() => {
            this.previousContractNodes = NodeFactory.createOrUpdateNodes(nodes, [], this.sourceStore.sources);

            this.previousContractInputs.forEach(input => {
                input.setIsReadOnly(true);
                input.setIsControlsHidden(true);

                if (InstanceUtils.isInputWithSource(input)) {
                    input.fetchSourceOptions();
                }
            });
        });
    }

    private filterComparisonLayers(nodes: NodeBaseModel[], layerId: string) {
        nodes.forEach(node => {
            if (InstanceUtils.isLayersNode(node) || InstanceUtils.isLayersWithSectionsNode(node)) {
                node.filterLayers([layerId]);
            }

            // Contract Renewal can be nested beneath the shared Basic General Information node.
            this.filterComparisonLayers(node.childNodes, layerId);
        });
    }

    private cancelFetchDataRequest() {
        if (this.fetchDataCancelSource) {
            this.fetchDataCancelSource.cancel();
            this.fetchDataCancelSource = null;
        }
    }

    private isFetchDataRequestCurrent(cancelSource: CancelTokenSource) {
        return this.fetchDataCancelSource === cancelSource;
    }

    private cancelSummaryRequest() {
        if (this.summaryCancelSource) {
            this.summaryCancelSource.cancel();
            this.summaryCancelSource = null;
        }
    }

    private isSummaryRequestCurrent(cancelSource: CancelTokenSource) {
        return this.summaryCancelSource === cancelSource;
    }

    private get summaryPayloadState(): SummaryPayloadState | null {
        const matching = this.sectionMatching;
        const matches = matching?.confirmedMatches;

        if (!this.comparisonNodes.length || (matching && (!matching.isConfirmed || !matches))) {
            return null;
        }

        const builder = new ContractComparisonSummaryPayloadBuilder(
            matching && matches
                ? {
                      context: matching.context,
                      matches
                  }
                : undefined
        );

        return builder.build(this.comparisonNodes);
    }

    private get summaryPayloadJson(): string | null {
        return this.summaryPayloadState?.requestJson ?? null;
    }

    private async prepareComparison(nodes: NodeBaseDto[]) {
        this.cancelFetchDataRequest();
        this.cancelSummaryRequest();
        this.summaryRefreshPromise = null;
        this.summaryRefreshPayloadHash = null;

        const cancelSource = axios.CancelToken.source();
        this.fetchDataCancelSource = cancelSource;

        runInAction(() => {
            this.isLoading = true;
            this.fetchPreviousContractStatus = 'idle';
            this.previousContractNodes = [];
            this.clearSummaryState();
        });
        this.initializeNewContractNodes(nodes);

        try {
            const hasLoadedPreviousContract = await this.loadPreviousContractData(cancelSource);

            if (!hasLoadedPreviousContract || !this.isFetchDataRequestCurrent(cancelSource)) {
                return;
            }

            this.createComparisonNodes();

            if (!this.store.isComparisonSummaryConfigured) {
                return;
            }

            await Promise.race([
                this.generateContractComparisonSummary(),
                ContractComparisonModel.wait(summaryPreparationTimeoutMs)
            ]);
        } finally {
            if (this.isFetchDataRequestCurrent(cancelSource)) {
                runInAction(() => {
                    this.isLoading = false;
                });
                this.fetchDataCancelSource = null;
            }
        }
    }

    private refreshComparison(nodes: NodeBaseDto[]) {
        this.initializeNewContractNodes(nodes);
        this.sectionMatching?.refresh();

        this.createComparisonNodes();
    }

    private async loadPreviousContractData(cancelSource: CancelTokenSource): Promise<boolean> {
        try {
            const response = await this.store.fetchPreviousContractData(
                {
                    businessId: this.businessId!,
                    layerId: this.parameters.layerId,
                    layoutType: this.parameters.layoutType
                },
                cancelSource.token
            );

            if (!this.isFetchDataRequestCurrent(cancelSource)) {
                return false;
            }

            if (!response.isOk()) {
                runInAction(() => {
                    this.fetchPreviousContractStatus = 'failed';
                });
                return false;
            }

            let hasLoadedPreviousContract = false;

            response.map(result => {
                runInAction(() => {
                    if (result.status === 'BusinessNotFound') {
                        this.fetchPreviousContractStatus = 'notFound';
                        return;
                    }

                    if (!this.isSectionMatchingValid(result.sectionMatching)) {
                        this.fetchPreviousContractStatus = 'failed';
                        return;
                    }

                    this.initializePreviousContractNodes(result.nodes);
                    this.sectionMatching = result.sectionMatching
                        ? new SectionMatchingModel(this, this.store, result.sectionMatching)
                        : null;

                    this.fetchPreviousContractStatus = 'idle';
                    hasLoadedPreviousContract = true;
                });
            });

            return hasLoadedPreviousContract;
        } catch (error) {
            if (!axios.isCancel(error) && this.isFetchDataRequestCurrent(cancelSource)) {
                console.error(error);
                runInAction(() => {
                    this.fetchPreviousContractStatus = 'failed';
                });
            }

            return false;
        }
    }

    private isSectionMatchingValid(sectionMatching?: ContractComparisonSectionsResultDto | null) {
        switch (this.parameters.layoutType) {
            case 'Sections':
                return !!sectionMatching;
            case 'LayersWithSections':
                return (
                    !!sectionMatching &&
                    !!this.parameters.layerId &&
                    sectionMatching.context.layerId === this.parameters.layerId
                );
            default:
                return true;
        }
    }

    private ensureSummaryRefreshPromise(targetPayloadHash: number): Promise<void> {
        if (this.summaryRefreshPromise && this.summaryRefreshPayloadHash === targetPayloadHash) {
            return this.summaryRefreshPromise;
        }

        const summaryRefreshPromise = this.waitForSummaryInputsAndGenerate(targetPayloadHash).finally(() => {
            if (this.summaryRefreshPromise === summaryRefreshPromise) {
                this.summaryRefreshPromise = null;
                this.summaryRefreshPayloadHash = null;
            }
        });

        this.summaryRefreshPromise = summaryRefreshPromise;
        this.summaryRefreshPayloadHash = targetPayloadHash;

        return summaryRefreshPromise;
    }

    private async waitForSummaryInputsAndGenerate(targetPayloadHash: number) {
        let currentTargetPayloadHash = targetPayloadHash;

        while (currentTargetPayloadHash !== null) {
            while (this.summaryPayloadHash === currentTargetPayloadHash && this.isAnyInputLoading) {
                await ContractComparisonModel.wait(summaryRefreshPollIntervalMs);
            }

            const latestPayloadHash = this.summaryPayloadHash;

            if (latestPayloadHash === null) {
                return;
            }

            if (latestPayloadHash !== currentTargetPayloadHash) {
                currentTargetPayloadHash = latestPayloadHash;
                this.summaryRefreshPayloadHash = latestPayloadHash;
                continue;
            }

            await this.requestCurrentSummary(currentTargetPayloadHash);
            return;
        }
    }

    private async requestCurrentSummary(targetPayloadHash: number) {
        const summaryPayloadState = this.summaryPayloadState;
        const summaryPayloadJson = this.summaryPayloadJson;

        if (
            summaryPayloadState === null ||
            summaryPayloadJson === null ||
            summaryPayloadState.signatureHash !== targetPayloadHash ||
            this.lastCompletedSummaryPayloadHash === targetPayloadHash
        ) {
            return;
        }

        if (this.isSummaryLoading && this.lastRequestedSummaryPayloadHash === targetPayloadHash) {
            return;
        }

        this.cancelSummaryRequest();
        const cancelSource = axios.CancelToken.source();
        this.summaryCancelSource = cancelSource;

        runInAction(() => {
            this.lastRequestedSummaryPayloadHash = targetPayloadHash;
            this.isSummaryLoading = true;
        });

        try {
            const response = await this.store.generateContractComparisonSummary(summaryPayloadJson, cancelSource.token);

            if (!this.isSummaryRequestCurrent(cancelSource) || this.summaryPayloadHash !== targetPayloadHash) {
                runInAction(() => {
                    this.lastRequestedSummaryPayloadHash = null;
                });
                return;
            }

            if (!response.isOk()) {
                runInAction(() => {
                    this.generatedSummary = null;
                    this.lastCompletedSummaryPayloadHash = targetPayloadHash;
                    this.lastGeneratedSummaryPayloadHash = null;
                    this.lastRequestedSummaryPayloadHash = null;
                });
                return;
            }

            response.map(result => {
                runInAction(() => {
                    this.generatedSummary = result.summary;
                    this.lastCompletedSummaryPayloadHash = targetPayloadHash;
                    this.lastGeneratedSummaryPayloadHash = result.summary ? targetPayloadHash : null;
                });
            });
        } catch (error) {
            if (!axios.isCancel(error) && this.isSummaryRequestCurrent(cancelSource)) {
                console.error('Error while generating contract comparison summary: ', error);
                runInAction(() => {
                    this.generatedSummary = null;
                    this.lastCompletedSummaryPayloadHash = targetPayloadHash;
                    this.lastGeneratedSummaryPayloadHash = null;
                    this.lastRequestedSummaryPayloadHash = null;
                });
            }
        } finally {
            if (this.isSummaryRequestCurrent(cancelSource)) {
                runInAction(() => {
                    this.isSummaryLoading = false;
                });
                this.summaryCancelSource = null;
            }
        }
    }

    private refreshSummaryState() {
        if (this.summaryPayloadHash !== this.lastGeneratedSummaryPayloadHash) {
            this.generatedSummary = null;
        }
    }

    private clearSummaryState() {
        this.generatedSummary = null;
        this.lastRequestedSummaryPayloadHash = null;
        this.lastCompletedSummaryPayloadHash = null;
        this.lastGeneratedSummaryPayloadHash = null;
    }
}
